// Pure derivations from raw API payloads. No side effects.

import { inRange } from './dateRange';

export const NEW_HOST_WINDOW_MS = 30 * 86400000;

export const PMS_OPTIONS = [
  { id: 'all', label: 'All PMS' },
  { id: 'guesty', label: 'Guesty' },
  { id: 'hospitable', label: 'Hospitable' },
  { id: 'track', label: 'Track' },
  { id: 'streamline', label: 'Streamline' },
  { id: 'manual', label: 'Direct / Manual' },
];

export const PMS_LABELS = {
  guesty: 'Guesty',
  hospitable: 'Hospitable',
  track: 'Track',
  streamline: 'Streamline',
  manual: 'Direct / Manual',
  unknown: 'Unknown',
};

// Derive PMS for a host. Prefers backend-provided `pmsType` (proposal §A);
// falls back to checking common token fields if the backend exposes them.
export function getHostPmsType(host) {
  if (!host) return 'unknown';
  if (host.pmsType) return host.pmsType;
  if (host.guestyAccessToken || host.guesty_client_id) return 'guesty';
  if (host.hospitableAccessToken || host.hospitableConnectedAt) return 'hospitable';
  if (host.trackKey || host.trackConnectedAt) return 'track';
  if (host.streamlineHostId) return 'streamline';
  return 'unknown';
}

export const isHostActive = (host) => Boolean(host?.isVerified && host?.isOnBoarded);
export const isHostNew = (host) => {
  if (!host?.createdAt) return false;
  return Date.now() - new Date(host.createdAt).getTime() < NEW_HOST_WINDOW_MS;
};

export function getHostName(host) {
  const full = `${host?.firstName || ''} ${host?.lastName || ''}`.trim();
  return full || host?.companyName || host?.email || 'Unnamed host';
}

// Per-order revenue: sum all fee buckets minus discount.
// Falls back gracefully if any field is missing.
export function getOrderRevenue(o) {
  if (!o) return 0;
  const sum =
    Number(o.groceryFee || 0) +
    Number(o.driverFee || 0) +
    Number(o.bellhoptFee || 0) +
    Number(o.hostFee || 0) +
    Number(o.tip || 0) +
    Number(o.alcoholFee || 0) +
    Number(o.insuranceFee || 0) -
    Number(o.discount || 0);
  return Number.isFinite(sum) ? sum : 0;
}

export const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

export const fmtNumber = (n) => Number(n || 0).toLocaleString('en-US');

export const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return String(v);
};

// Build a derived host row used across the dashboard.
export function enrichHost(host) {
  const active = isHostActive(host);
  const isNew = isHostNew(host);
  return {
    ...host,
    pmsType: getHostPmsType(host),
    active,
    isNew,
    propertyCount: Number(host?.propertyCount || 0),
    name: getHostName(host),
  };
}

// Derive PMS distribution (Map<pmsType, hostCount>).
export function pmsDistribution(hosts) {
  const m = new Map();
  hosts.forEach((h) => {
    const k = h.pmsType || 'unknown';
    m.set(k, (m.get(k) || 0) + 1);
  });
  return m;
}

// Per-host order aggregates over a list of orders. Returns Map<hostId, {orders,revenue,aov}>.
// Order rows must already be hydrated with property.hostID via /orders endpoint.
export function aggregateOrdersByHost(orders, hostIdByPropertyId) {
  const m = new Map();
  orders.forEach((o) => {
    const propId = String(o.propertyID || o?.property?._id || '');
    const hostId =
      String(o?.property?.hostID || '') ||
      hostIdByPropertyId?.get(propId) ||
      '';
    if (!hostId) return;
    const cur = m.get(hostId) || { orders: 0, revenue: 0 };
    cur.orders += 1;
    cur.revenue += getOrderRevenue(o);
    m.set(hostId, cur);
  });
  m.forEach((v) => {
    v.aov = v.orders > 0 ? v.revenue / v.orders : 0;
  });
  return m;
}

// Filter hosts by current dashboard filter state.
export function filterHostsByState(enrichedHosts, state) {
  return enrichedHosts.filter((h) => {
    if (state.hostType === 'new' && !h.isNew) return false;
    if (state.hostType === 'legacy' && h.isNew) return false;
    if (state.pms !== 'all' && h.pmsType !== state.pms) return false;
    if (state.hostQuery) {
      const q = state.hostQuery.toLowerCase();
      const hay = `${h.name} ${h.email || ''} ${h.companyName || ''} ${h.phoneNumber || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function newlyOnboardedHosts(hosts, range) {
  return hosts.filter((h) => inRange(h.createdAt, range.start, range.end));
}

// --- Drivers ---
// Mongoose `{ timestamps: true }` on the Driver schema sets `createdAt` once
// at insert and never mutates it (only `updatedAt` updates), so we can treat
// `driver.createdAt` as the signup/acquisition timestamp.

export function getDriverName(d) {
  const full = `${d?.firstName || ''} ${d?.lastName || ''}`.trim();
  return full || d?.email || 'Unnamed driver';
}

export const isDriverActive = (d) =>
  Boolean(d?.isVerified && d?.isOnBoarded && (d?.checkrVerified ? d.checkrVerified === 'clear' : true));

export function enrichDriver(d) {
  return {
    ...d,
    name: getDriverName(d),
    active: isDriverActive(d),
    market: d?.market || d?.address?.city || '—',
  };
}

export function newlyAcquiredDrivers(drivers, range) {
  return drivers.filter((d) => inRange(d?.createdAt, range.start, range.end));
}
